/**
 * Werkstatt-Zweig „Performance" — Karten-Öffnen-Latenz + Resource-Leak-Test.
 *
 * Anlassfall 2026-05-19 (Thomas): nach Strg+R lässt sich die erste Karte
 * schnell öffnen, dann jede nachfolgende langsamer / gar nicht. Hypothese:
 * Background-Requests (`preview` PDF-Render auf OCR-Sidecar dauert mehrere
 * Sek, `cell-image-keys`, `grid-bboxes`) werden beim Modal-Close nicht
 * abgebrochen → akkumulieren in den 6 Browser-Connection-Slots → blocken
 * den nächsten Klick. Reproduzierbar via Playwright: 14 unfinished requests
 * nach 3 sequenziell geöffneten Karten.
 *
 * Test misst die Latenz des EXACT-DetailPage-Fetch-Patterns sequenziell:
 *   pro Karte:  timesheet.get + Promise.all(cell-image-keys + grid-bboxes + preview)
 *
 * Verdict-Schwellen:
 *   • Karte > 5 s     → BLOCKED
 *   • Drift erste/letzte > 2 s → CONNECTION_POOL_BLOCK (sequenz wird langsamer)
 *   • sonst           → OK
 *
 * Aktueller Stand: REJECTED (kein AbortController → preview/cell-image-keys/
 * grid-bboxes leaken). Fix-Plan: Iteration danach.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DetailModal } from "../components/DetailModal";
import { WerkstattBranchHeader } from "../components/werkstatt/WerkstattBranchHeader";
import { PipelineStepList, type PipelineStep } from "../components/SettingsTestOcr";
import { api } from "../lib/api";
import { useSelectedMonth } from "../lib/useSelectedMonth";
import { useClientContext } from "../lib/ClientContext";
import { stepUuid } from "../lib/stepUuid";

interface PerCard {
  card_id: string;
  ts_ms: number | null;       // timesheet.get latency
  parallel_ms: number | null; // Promise.all(keys + grid + preview)
  total_ms: number | null;
  error?: string;
}

type Verdict = "OK" | "BLOCKED" | "CONNECTION_POOL_BLOCK";

const SLOW_CARD_MS = 5000;
const DRIFT_MS = 2000;

export function WerkstattPerformancePage({ modal }: { modal?: boolean } = {}) {
  const loc = useLocation();
  const nav = useNavigate();
  const background = (loc.state as { background?: Location } | null)?.background;
  function closeModal() {
    if (background) nav(-1);
    else nav("/board");
  }
  const { selectedClientId } = useClientContext();
  const { month } = useSelectedMonth();
  const [cardPool, setCardPool] = useState<string[]>([]);
  const [phase, setPhase] = useState<"idle" | "running" | "done" | "error">("idle");
  const [perCard, setPerCard] = useState<PerCard[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Lade die ersten 5 timesheet-IDs des Monats — Test braucht echte Karten
  // mit existierenden PDFs damit preview + grid-bboxes nicht 404 returnt.
  useEffect(() => {
    let alive = true;
    api.cards.list(month, "0", selectedClientId, "timesheet").then((r) => {
      if (!alive) return;
      const ids = r.cards.map((c) => c.id).slice(0, 5);
      setCardPool(ids);
    }).catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [month, selectedClientId]);

  async function run() {
    if (cardPool.length < 2) {
      setErr("Mind. 2 Karten im Pool nötig");
      return;
    }
    setPhase("running");
    setErr(null);
    setPerCard([]);
    const out: PerCard[] = [];
    for (const cardId of cardPool) {
      const t0 = performance.now();
      try {
        const t1 = performance.now();
        await api.timesheets.get(cardId);
        const tsMs = performance.now() - t1;
        const t2 = performance.now();
        await Promise.all([
          api.timesheets.cellImageKeys(cardId).catch(() => null),
          fetch(`api/cards/timesheet/${cardId}/grid-bboxes`, { credentials: "same-origin" }).then((r) => r.json()).catch(() => null),
          fetch(`api/cards/timesheet/${cardId}/preview-cached`, { credentials: "same-origin" }).then((r) => r.blob()).catch(() => null),
        ]);
        const parMs = performance.now() - t2;
        out.push({
          card_id: cardId,
          ts_ms: Math.round(tsMs),
          parallel_ms: Math.round(parMs),
          total_ms: Math.round(performance.now() - t0),
        });
      } catch (e: any) {
        out.push({ card_id: cardId, ts_ms: null, parallel_ms: null, total_ms: null, error: e?.message ?? String(e) });
      }
      setPerCard([...out]);  // Live-Update während Test läuft
    }
    setPhase("done");
  }

  const verdict: Verdict | null = useMemo(() => {
    if (phase !== "done" || perCard.length < 2) return null;
    const totals = perCard.map((c) => c.total_ms ?? 99999);
    if (totals.some((t) => t > SLOW_CARD_MS)) return "BLOCKED";
    const drift = totals[totals.length - 1]! - totals[0]!;
    if (drift > DRIFT_MS) return "CONNECTION_POOL_BLOCK";
    return "OK";
  }, [phase, perCard]);

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Karten-Pool laden (erste 5 timesheets des Monats)",
      description: "GET /api/cards?type=timesheet&month=… — Test braucht reale Karten "
        + "damit preview / grid-bboxes / cell-image-keys nicht 404 returnen.",
      status: cardPool.length >= 2 ? "done" : err ? "error" : "pending",
      detail: cardPool.length >= 2
        ? `${cardPool.length} Karten im Pool: ${cardPool.map((id) => id.slice(0, 8)).join(", ")}`
        : err ? `Fehler: ${err}` : "(lädt)",
    },
    {
      num: 2,
      title: "Sequenz-Test: pro Karte das DetailPage-Fetch-Pattern messen",
      description:
        "Pro Karte: timesheet.get + Promise.all(cell-image-keys, grid-bboxes, preview). "
        + "Misst Latenz wie sie der User beim Karten-Öffnen erlebt. "
        + "Sequenzielle Ausführung simuliert den realen Modal-Flow (Karte 1 → schließen → "
        + "Karte 2 → schließen → ...) — Connection-Pool-Block wird sichtbar weil ohne "
        + "AbortController jede Karte stale Background-Requests hinterlässt.",
      status: phase === "running" ? "active" : phase === "done" ? "done" : "pending",
      detail: (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            type="button"
            onClick={run}
            disabled={phase === "running" || cardPool.length < 2}
            style={{
              padding: "6px 14px", fontSize: 13, fontWeight: 600,
              background: phase === "running" ? "#9ca3af" : "#1d4ed8",
              color: "white", border: 0, borderRadius: 4,
              cursor: phase === "running" ? "wait" : "pointer",
              alignSelf: "flex-start",
            }}
          >
            {phase === "running" ? "läuft …" : phase === "done" ? "🔁 Erneut testen" : "Test starten"}
          </button>
          {err && <div style={{ color: "#991b1b", fontSize: 12 }}>{err}</div>}
          {perCard.length > 0 && (
            <table className="data-table" style={{ fontSize: 12, marginTop: 4 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Karte</th>
                  <th>timesheet.get (ms)</th>
                  <th>parallel (keys+grid+preview, ms)</th>
                  <th>total (ms)</th>
                </tr>
              </thead>
              <tbody>
                {perCard.map((r, i) => (
                  <tr key={i} style={{
                    background: r.total_ms != null && r.total_ms > SLOW_CARD_MS ? "#fef2f2" : undefined,
                  }}>
                    <td>{i + 1}</td>
                    <td><code>{r.card_id.slice(0, 8)}</code></td>
                    <td>{r.ts_ms ?? "—"}</td>
                    <td>{r.parallel_ms ?? "—"}</td>
                    <td><strong>{r.total_ms ?? "—"}</strong>{r.error ? ` (Fehler: ${r.error})` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ),
    },
    {
      num: 3,
      title: "Verdict",
      description:
        `Schwellen: Karte > ${SLOW_CARD_MS}ms = BLOCKED · Drift (letzte − erste) > ${DRIFT_MS}ms = `
        + "CONNECTION_POOL_BLOCK (Anzeichen für leakende Background-Requests die nachfolgende "
        + "Karten blockieren).",
      status: verdict === "OK" ? "done" : verdict ? "error" : "pending",
      detail: !verdict ? "(wartet auf Test-Lauf)" : (
        <VerdictBox verdict={verdict} perCard={perCard} />
      ),
    },
  ];

  const stepsWithUuids = steps.map((s) => ({
    ...s,
    uuid: stepUuid("performance", "karten-open", s.num),
  }));

  const inner = (
    <div>
      <WerkstattBranchHeader activeBranch="performance" modal={modal} />
      <div style={{ padding: "8px 16px 16px" }}>
        <h2 style={{ marginTop: 0 }}>Karten-Öffnen-Performance</h2>
        <p className="muted" style={{ marginBottom: 16, fontSize: 13 }}>
          Bug 2026-05-19: nach Strg+R erste Karte schnell, danach blockiert.
          Test reproduziert das Fetch-Pattern einer DetailPage über 5 Karten in Folge.
          Aktuell <strong>REJECTED</strong> — Background-Requests (preview, cell-image-keys,
          grid-bboxes) werden beim Modal-Close nicht abgebrochen, akkumulieren in den
          Browser-Connection-Slots, blocken nachfolgende Karten.
        </p>
        <PipelineStepList steps={stepsWithUuids} />
      </div>
    </div>
  );

  if (modal) {
    return <DetailModal dirty={false} onClose={closeModal}>{inner}</DetailModal>;
  }
  return inner;
}

function VerdictBox({ verdict, perCard }: { verdict: Verdict; perCard: PerCard[] }) {
  const totals = perCard.map((c) => c.total_ms ?? 0);
  const max = Math.max(...totals);
  const drift = totals[totals.length - 1]! - totals[0]!;
  const tone = verdict === "OK"
    ? { bg: "#ecfdf5", fg: "#065f46", border: "#86efac", icon: "✅" }
    : { bg: "#fef2f2", fg: "#991b1b", border: "#fca5a5", icon: "❌" };
  return (
    <div style={{
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
      padding: 10, borderRadius: 4, fontSize: 13,
    }}>
      <strong>{tone.icon} {verdict}</strong>
      <div style={{ marginTop: 4 }}>
        max Karte: <strong>{max}ms</strong> · Drift erste→letzte: <strong>{drift}ms</strong>
      </div>
      {verdict === "CONNECTION_POOL_BLOCK" && (
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Sequenz wird langsamer — typisch für leakende Background-Requests die die
          Browser-Connection-Slots blockieren. Fix: AbortController auf alle async
          fetches in TimesheetDetailPage (cell-image-keys, grid-bboxes, preview).
        </div>
      )}
      {verdict === "BLOCKED" && (
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Mindestens eine Karte hat &gt;{SLOW_CARD_MS}ms gebraucht. Backend zu langsam
          oder Connection-Pool völlig blockiert.
        </div>
      )}
    </div>
  );
}
